import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { inject } from 'mobx-react';
import TweenMax from 'TweenMax';
import StackBlur from 'stackblur-canvas';

import ResponsiveImage from './ResponsiveImage';
import AutoVideo from './AutoVideo';

const IMAGE = 'image';
const VIDEO = 'video';

export const mediaTypeIsValid = type => type === IMAGE || type === VIDEO;

const Styled = styled.div`
  position: relative;
  overflow: hidden;
  background-color: ${props => (props.showBackground ? '#eee' : 'transparent')};

  ${props =>
    props.fullHeight &&
    css`
      &,
      * {
        height: 100%;
      }
    `};
`;

const MediaContainer = styled.div`
  opacity: 0;

  ${props =>
    props.show &&
    css`
      opacity: 1;
    `};
`;

const Canvas = styled.canvas`
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  will-change: opacity;
  opacity: 0;
  transform: scale(1.05);
`;

class Media extends React.Component {
  static propTypes = {
    type: PropTypes.oneOf([IMAGE, VIDEO]),
    content: PropTypes.object.isRequired,
    progressStore: PropTypes.object.isRequired,
    mediaRef: PropTypes.func,
    fullHeight: PropTypes.bool,
    noPreview: PropTypes.bool,
    showBackground: PropTypes.bool,
  };

  notNeedOnload = false;

  state = {
    loaded: false,
  };

  componentDidMount() {
    this.props.progressStore.registerMedia();

    this.handlePreviewLoad();
  }

  componentWillUnmount() {
    if (this.notNeedOnload) {
      return;
    }
    this.sendOnLoadMessage();
  }

  handlePreviewLoad = () => {
    if (this.state.loaded) {
      return;
    }

    if (this.props.noPreview) {
      return;
    }

    if (!this.props.content.preview) {
      return;
    }

    const img = new Image();
    img.crossOrigin = 'Anonymous';
    const ctx = this.canvasNode.getContext('2d');
    const width = this.canvasNode.offsetWidth;
    const height = this.canvasNode.offsetHeight;
    this.canvasNode.width = width;
    this.canvasNode.height = height;

    const canvasNode = this.canvasNode;

    img.onload = () => {
      if (this.state.loaded) {
        return;
      }

      if (!width || !height) {
        return;
      }

      img.width = width;
      img.height = height;
      ctx.drawImage(img, 0, 0, width, height);
      StackBlur.canvasRGBA(canvasNode, 0, 0, width, height, 30);
      TweenMax.to(canvasNode, 2, { scale: 1, ease: Power1.easeIn });
      TweenMax.fromTo(canvasNode, 0.5, { alpha: 0 }, { alpha: 1, ease: Power1.easeIn });
    };

    img.src = this.props.content.preview;
  };

  handleMediaLoad = () => {
    this.setState({ loaded: true });

    if (this.notNeedOnload) {
      return;
    }
    this.sendOnLoadMessage();

    if (this.props.content.preview) {
      TweenMax.to(this.canvasNode, 0.5, { autoAlpha: 0, scale: 1, ease: Power1.easeOut });
    }
  };

  sendOnLoadMessage() {
    this.notNeedOnload = true;
    this.props.progressStore.onMediaLoad();
  }

  render() {
    const {
      className,
      type,
      content,
      content: { preview },
      mediaRef,
      fullHeight,
      showBackground,
      ...rest
    } = this.props;

    return (
      <Styled className={className} fullHeight={fullHeight} showBackground={showBackground}>
        <MediaContainer show={this.state.loaded}>
          {type === IMAGE ? (
            <ResponsiveImage {...rest} {...content} onLoad={this.handleMediaLoad} imgRef={mediaRef} />
          ) : (
            <AutoVideo {...rest} {...content} onLoad={this.handleMediaLoad} innerRef={mediaRef} />
          )}
        </MediaContainer>
        {preview && <Canvas key={preview} innerRef={node => (this.canvasNode = node)} />}
      </Styled>
    );
  }
}

export default inject(stores => ({
  progressStore: stores.store.progress,
}))(Media);



// WEBPACK FOOTER //
// ./src/components/Media.js